import React, { useEffect, useState } from "react";
import { Modal, Button, Input, Select } from "antd";
import { XIcon, Asterisk, AddMemeberSuccess, AddMemberError } from "../../../../assets/icons";
import * as Yup from "yup";
import { Formik } from "formik";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import { httpService } from "../../../../base/services/httpService.service";
import Toast from "../../../../base/components/Toast";
import { Spin, notification } from "antd";
import "./filters.scss";
import { useTranslation } from "../../../../i18n/LanguageContext";
import { translateRoleName } from "../../../auth/userRoles.enum";
import { translateGenderName } from "../../../../base/utils/genderLabels";

const AddMemeberModal = ({ visible, onCancel, onOk }) => {
   const { t } = useTranslation();
   const [gender, setGender] = useState([]);
   const [role, setRole] = useState([]);
   const [loading, setLoading] = useState(false);
   const formData = {
      fullName: "",
      email: "",
      birthDate: null,
      gender: "",
      roleId: ""
   };

   const validationSchema = Yup.object({
      fullName: Yup.string().required(t("name-required-validation")),
      email: Yup.string()
         .email(t("invalid-email-validation"))
         .required(t("email-required-validation")),
      birthDate: Yup.string().nullable(),
      gender: Yup.string().required(t("gender-required-validation")),
      roleId: Yup.string().required(t("role-required-validation"))
   });

   const getGenders = async () => {
      // Kept as raw {value, name} pairs (not pre-translated into `label`) so
      // the dropdown re-translates on a language switch without a refetch.
      await httpService.get("/StaffManagement/genders", (res) => {
         const data = res?.data?.map((gender) => {
            return {
               value: gender?.id,
               name: gender?.name
            };
         });
         setGender(data);
      });
   };

   const getRoles = async () => {
      await httpService.get("/StaffManagement/roles", (res) => {
         const data = res?.data?.map((role) => {
            return {
               value: role?.id,
               name: role?.name
            };
         });
         setRole(data);
      });
   };

   useEffect(() => {
      if (visible) {
         getGenders();
         getRoles();
      }
   }, [visible]);

   // Derived at render time (not baked into state) so switching language
   // re-translates the options immediately, without a network refetch.
   const genderOptions = gender.map((option) => ({
      value: option.value,
      label: translateGenderName(option.name, t)
   }));
   const roleOptions = role.map((option) => ({
      value: option.value,
      label: translateRoleName(option.name, t)
   }));

   const customNotificationSucess = (
      <div className="custom-notification-container">
         <div className="custom-notification-image-container">
            <img src={AddMemeberSuccess} alt="AddMemeberSuccess" />
         </div>
         <div className="custom-notification-text-container">
            <h3>{t("team-member-added-success-title")}</h3>
            <p>{t("team-member-added-success-description")}</p>
         </div>
      </div>
   );
   const customNotificationError = (
      <div className="custom-notification-container-error">
         <div className="custom-notification-image-container">
            <img src={AddMemberError} alt="AddMemberError" />
         </div>
         <div className="custom-notification-text-container-error">
            <h3>{t("toast-oops-title")}</h3>
            <p>{t("team-member-add-error-description")}</p>
         </div>
      </div>
   );

   const openNotificationSuccess = () => {
      notification.open({
         description: customNotificationSucess,
         placement: "top"
      });
   };

   const openNotificationError = () => {
      notification.open({
         description: customNotificationError,
         placement: "top"
      });
   };

   const handleSubmit = async (values) => {
      try {
         setLoading(true);
         const response = await httpService.post("/StaffManagement", values);
         onOk();
         openNotificationSuccess();
      } catch (error) {
         if (error.response && error.response.data && error.response.data.errors) {
            const errorMessage = error.response.data.errors[0].message;
            Toast.error(errorMessage);
            openNotificationError();
         }
      } finally {
         setLoading(false);
      }
   };

   return (
      <Modal open={visible} onCancel={onCancel} footer={null} closable={false} destroyOnClose>
         <Formik
            initialValues={formData}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
         >
            {({ errors, values, handleSubmit, handleChange, setFieldValue, touched }) => (
               <form onSubmit={handleSubmit}>
                  <div>
                     <img
                        src={XIcon}
                        alt="XIcon"
                        onClick={onCancel}
                        style={{ float: "right", cursor: "pointer" }}
                     />
                     <div className="add-representative-details">
                        <h1>{t("add-new-member-title")}</h1>
                     </div>
                     <div>
                        <div className="label-name-add-representative-details">
                           <img src={Asterisk} alt="Asterisk" />
                           <label>{t("name")}</label>
                        </div>
                        <Input
                           autoComplete="off"
                           value={values.fullName}
                           type="text"
                           name="fullName"
                           onChange={handleChange}
                           placeholder={t("name")}
                        />
                     </div>
                     {errors.fullName && touched.fullName && (
                        <div className="error-message">{errors.fullName}</div>
                     )}
                     <div>
                        <div className="label-name-add-representative-details">
                           <img src={Asterisk} alt="Asterisk" />
                           <label>{t("email")}</label>
                        </div>
                        <Input
                           autoComplete="off"
                           value={values.email}
                           type="email"
                           name="email"
                           onChange={handleChange}
                           placeholder={t("email")}
                        />
                     </div>
                     {errors.email && touched.email && (
                        <div className="error-message">{errors.email}</div>
                     )}

                     <div>
                        <div className="label-name-add-representative-details">
                           <label>{t("birthdate-label")}</label>
                        </div>
                        <DatePicker
                           name="birthDate"
                           value={values.birthDate ? dayjs(values.birthDate) : null}
                           onChange={(date) =>
                              setFieldValue("birthDate", date ? date.format("YYYY-MM-DD") : null)
                           }
                           style={{ width: "100%" }}
                        />
                     </div>
                     <div>
                        <div className="label-name-add-representative-details">
                           <img src={Asterisk} alt="Asterisk" />
                           <label>{t("gender-label")}</label>
                        </div>
                        <Select
                           options={genderOptions}
                           value={genderOptions.find((option) => option.value === values.gender)}
                           onChange={(selectedOption) => setFieldValue("gender", selectedOption)}
                           placeholder={t("select-gender-placeholder")}
                           style={{ width: "100%" }}
                        />

                        {errors.gender && touched.gender && (
                           <div className="error-message">{errors.gender}</div>
                        )}
                     </div>
                     <div>
                        <div className="label-name-add-representative-details">
                           <img src={Asterisk} alt="Asterisk" />
                           <label>{t("role-label")}</label>
                        </div>
                        <Select
                           options={roleOptions}
                           value={roleOptions.find((option) => option.value === values.roleId)}
                           onChange={(selectedOption) => setFieldValue("roleId", selectedOption)}
                           placeholder={t("select-role-placeholder")}
                           style={{ width: "100%" }}
                        />
                        {errors.roleId && touched.roleId && (
                           <div className="error-message">{errors.roleId}</div>
                        )}
                     </div>
                     <div className="footer-buttons-add-representative-details" key="uniqueKey">
                        <Button className="cencel-delete-button" key="cancel" onClick={onCancel}>
                           {t("cancel")}
                        </Button>
                        <Button
                           className="delete-button"
                           type="submit"
                           onClick={handleSubmit}
                           disabled={loading}
                        >
                           {loading ? <Spin /> : t("save-changes-button")}
                        </Button>
                     </div>
                  </div>
               </form>
            )}
         </Formik>
      </Modal>
   );
};

export default AddMemeberModal;
